import {
  UserCircleIcon,
  ExternalIcon,
  useFilter,
  FilterControls,
  DataTable as Table,
  Indicator,
  ExclamationIcon,
  formatMoney,
  IndicatorProps,
  CheckInCircleIcon,
  CrossInCircleIcon,
  ReloadIcon,
} from '@setel/portal-ui';
import tw from 'twin.macro';
import { useGetUserById } from '../../accounts/accounts.queries';
import * as React from 'react';
import { LoadingPage } from 'modules/pages/loading-page';
import { environments } from 'environments/environment';
import { AxiosError } from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { useIndexCustomerTransactions } from '../topup-and-payment.queries';
import { ITransaction } from 'shared/interfaces/transaction.interface';
import { UserNotFoundPage } from 'modules/accounts/account-details';
import { GetIndicatorStatusColor } from '../topup-and-payments.helper';
import { pageview } from 'shared/analytics/gtag';

export function TopupAndPaymentListing({ userId }: { userId: string }) {
  // added ga for url tracking
  let location = useLocation();
  React.useEffect(() => {
    pageview(location.pathname, 'Top Up and Payment');
  }, [location]);

  const { data, isError, isLoading } = useGetUserById(userId, {
    retry: (retryCount, err) => {
      const error = err as AxiosError;
      return retryCount < 3 && error?.response?.status !== 404;
    },
  });

  return (
    <>
      {isLoading && !data ? (
        <LoadingPage />
      ) : isError ? (
        <UserNotFoundPage />
      ) : (
        <React.Fragment>
          <div
            style={{ backgroundColor: '#f5f5f5' }}
            css={tw`flex justify-between px-4 items-center py-4 border-gray-200 border border-b-0`}
          >
            <div css={tw`inline-flex h-10 items-center`}>
              <UserCircleIcon css={tw`text-3xl text-gray-500 mr-3`} />
              <div css={tw`flex flex-col`}>
                <div className="text-base">{data?.fullName}</div>
                <div className="text-base text-lightgrey">{data?.phone}</div>
              </div>
            </div>
          </div>

          <TopupAndPaymentInfo userId={userId} isUserProfileLoaded={!!data} />
        </React.Fragment>
      )}
    </>
  );
}

export function TopupAndPaymentInfo({
  userId,
  isUserProfileLoaded,
}: {
  userId: string;
  isUserProfileLoaded: boolean;
}) {
  const filter = useFilter<{ createdAt: [string, string] }>(
    {
      createdAt: ['', ''],
    },
    {
      components: [
        {
          key: 'createdAt',
          type: 'daterange',
          props: {
            label: 'Created on',
            placeholder: 'Any dates',
            disableFuture: true,
          },
        },
      ],
    }
  );

  const {
    data: topupCard,
    isError,
    isLoading: isLoadingTopup,
    refetch,
  } = useIndexCustomerTransactions(
    userId,
    {
      from: filter[0].values.createdAt?.[0],
      to: filter[0].values.createdAt?.[1],
    },
    {
      enabled: isUserProfileLoaded,
      retry: (retryCount, err) => {
        const error = err as AxiosError;
        return error.response?.status !== 404 && retryCount < 3;
      },
    }
  );

  return (
    <>
      <div css={tw`border-gray-200 border`}>
        {isError && (
          <div className="break-words text-lg m-3">Topup card not found</div>
        )}

        <React.Fragment>
          <FilterControls
            className={'pb-4'}
            css={tw`shadow-none`}
            filter={filter}
          />
          <div className={'mb-4 text-center flex justify-center'}>
            <p
              className="text-brand-500 tracking-1 font-bold pu-cursor-pointer leading-4"
              onClick={() => refetch()}
            >
              <span className="text-xs">REFRESH LIST</span>
              <ReloadIcon
                className="ml-2 inline"
                style={{ width: 16, height: 16 }}
              />
            </p>
          </div>
          <div
            css={[tw`overflow-y-auto relative`, 'max-height:283px']}
            data-react-setel-scrollbar
          >
            <Table
              type="secondary"
              isLoading={isLoadingTopup}
              data-testid="topup-table"
            >
              <Table.Thead>
                <Table.Tr css={tw`sticky top-0 h-12`}>
                  <Table.Th css={tw`sticky top-0`}>Merchant name</Table.Th>
                  <Table.Th css={tw`sticky top-0 text-right`}>AMOUNT</Table.Th>
                </Table.Tr>
              </Table.Thead>
              {!isLoadingTopup && topupCard && (
                <Table.Tbody>
                  {topupCard?.map((topup: ITransaction) => (
                    <Table.Tr
                      key={topup.id}
                      render={(props) => (
                        <Link
                          {...props}
                          to={`/topup-and-payment/${userId}/topup/${topup.id}`}
                          data-testid={`topup-table-by-row-${topup.id}`}
                        ></Link>
                      )}
                      css={tw`h-12`}
                    >
                      <Table.Td>
                        {topup.meta?.merchantName}
                      </Table.Td>
                      <Table.Td className={'flex justify-between'}>
                        <Indicator
                          color={GetIndicatorStatusColor[topup.status]}
                          key={'success'}
                          icon={Icons[topup.status]}
                          css={tw`w-3.5 h-3.5`}
                        />
                        <div className="inline">
                          {topup?.amount
                            ? ` RM${formatMoney(topup.amount)}`
                            : `-`}
                        </div>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              )}
              {!isLoadingTopup && (
                <>
                  {(!topupCard || isError || !topupCard.length) && (
                    <Table.Caption>
                      <div className="py-12">
                        <p className="text-center text-gray-400 text-sm">
                          No data available.
                        </p>
                      </div>
                    </Table.Caption>
                  )}
                </>
              )}
            </Table>
          </div>
          <a
            href={`${environments.webAdminUrl}/customers/${userId}?tabIndex=3`}
            className="text-brand-500 tracking-1 font-semibold my-4 text-center flex justify-center"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="text-xs">VIEW MORE </span>
            <ExternalIcon className="ml-2 inline" />
          </a>
        </React.Fragment>
      </div>
    </>
  );
}

const Icons: Record<string, IndicatorProps['icon']> = {
  success: <CheckInCircleIcon css={tw`inline w-3.5 h-3.5`} />,
  error: <CrossInCircleIcon css={tw`inline w-3.5 h-3.5 `} />,
  failed: <CrossInCircleIcon css={tw`inline w-3.5 h-3.5 `} />,
  pending: <ExclamationIcon css={tw`inline w-3.5 h-3.5 `} />,
  cancelled: <CrossInCircleIcon css={tw`inline w-3.5 h-3.5 `} />,
  incoming: <ExclamationIcon css={tw`inline w-3.5 h-3.5 `} />,
  reversed: <ExclamationIcon css={tw`inline w-3.5 h-3.5 `} />,
  expired: <CrossInCircleIcon css={tw`inline w-3.5 h-3.5 `} />,
};
